import React, { useEffect, useRef, useState } from 'react';
import { Diamond } from '@/components/Ornaments';
import { cn } from '@/lib/utils';

const easeOutQuart = (t: number) => 1 - (--t) * t * t * t;
const frameDuration = 1000 / 60;

type CountUpAnimationProps = {
    countTo: number;
    duration?: number;
};

//Snippet from: https://jshakespeare.com/simple-count-up-number-animation-javascript-react/
const CountUpAnimation = ({ countTo, duration = 1250 }: CountUpAnimationProps) => {
    const [count, setCount] = useState(0);
    //the animation runs again whenever the target changes, so it has to know
    //where it currently stands without restarting itself on every frame
    const countRef = useRef(0);
    countRef.current = count;

    useEffect(() => {
        const startValue = countRef.current;
        let frame = 0;
        const totalFrames = Math.round(duration / frameDuration);
        const counter = setInterval(() => {
            frame++;
            const progress = easeOutQuart(frame / totalFrames);
            setCount(startValue + (countTo - startValue) * progress);

            if (frame >= totalFrames) {
                setCount(countTo);
                clearInterval(counter);
            }
        }, frameDuration);

        return () => clearInterval(counter);
    }, [countTo, duration]);

    return Math.floor(count).toLocaleString("en-US");
};

function NumberLoading() {
    return (
        <div className="h-6 w-24 animate-breathe rounded-bar bg-track" />
    )
}


export type PageCalloutProps = {
    label: string;
    icon: React.ReactNode;
    value: number | false;
    prefix?: string;
}

function PageCallout({ label, icon, value, prefix }: PageCalloutProps) {
    return (
        <div className="plate flex min-w-0 flex-col gap-1 px-3.5 py-3 transition-colors duration-150 hover:border-edge-strong">
            <span className="flex min-w-0 items-center justify-between gap-2.5 text-faint">
                <span className="flex min-w-0 items-center gap-1.5">
                    <Diamond />
                    <h3 className="truncate-flex font-rad text-label font-semibold uppercase tracking-[.12em] scrim">
                        {label}
                    </h3>
                </span>
                <span className="hidden shrink-0 drop-scrim xs:block">{icon}</span>
            </span>
            {value === false ? (
                <NumberLoading />
            ) : (
                <span className="truncate-flex block font-highlight text-value tabular leading-none scrim xs:text-title">
                    {prefix}<CountUpAnimation countTo={value} />
                </span>
            )}
        </div>
    );
}


export type PageCalloutRowProps = {
    callouts: PageCalloutProps[];
};
const calloutGridCols: Record<number, string> = {
    3: 'lg:grid-cols-3',
    4: 'lg:grid-cols-4',
    5: 'lg:grid-cols-3 2xl:grid-cols-5',
    6: 'lg:grid-cols-3 2xl:grid-cols-6',
    //7 and 8 stay at four per row: any wider and the labels start truncating
    7: 'lg:grid-cols-4',
    8: 'lg:grid-cols-4',
};

export default function PageCalloutRow({ callouts }: PageCalloutRowProps) {
    const gridCols = calloutGridCols[callouts.length];
    if (!gridCols) return null;

    return (
        <div className={cn(
            'grid px-2 md:px-0 gap-2 xs:gap-4 grid-cols-2 mb-4 md:mb-6',
            gridCols,
        )}>
            {callouts.map((callout) => (
                <PageCallout key={callout.label} {...callout} />
            ))}
        </div>
    )
}
